import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Search, Loader2, ArrowLeft, TrendingDown, TrendingUp, Star,
  Phone, MapPin, Euro, Clock, ExternalLink, Filter, ArrowRight,
  BarChart3,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

function fmt(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (Number.isNaN(n)) return String(v);
  return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n);
}
function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch (_e) { return null; }
}

const STATUT_BADGE = {
  prospect: 'bg-purple-100 text-purple-700',
  en_attente_docs: 'bg-amber-100 text-amber-700',
  en_examen: 'bg-blue-100 text-blue-700',
  actif: 'bg-green-100 text-green-700',
};
const STATUT_LABEL = {
  prospect: 'Candidat',
  en_attente_docs: 'Attend retour',
  en_examen: 'En examen',
  actif: 'Actif',
};

/**
 * Comparateur de tarifs entre sous-traitants pour une même prestation.
 * Parcours : Trade → Prestation → Tableau comparatif (tri prix croissant).
 */
export default function TarifsComparateur() {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [selectedCode, setSelectedCode] = useState(null);
  const [prestationSearch, setPrestationSearch] = useState('');
  const [comparison, setComparison] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [onlyActive, setOnlyActive] = useState(true);
  const [zoneFilter, setZoneFilter] = useState('');

  useEffect(() => { loadCatalog(); }, []);

  const loadCatalog = async () => {
    setLoading(true);
    try {
      const r = await api.get('/sous-traitants/tarifs/prestations');
      setCatalog(r.data.trades || []);
    } catch (e) {
      toast.error('Impossible de charger le catalogue');
    } finally { setLoading(false); }
  };

  const loadComparison = async (code) => {
    setSelectedCode(code);
    setLoadingComparison(true);
    try {
      const params = new URLSearchParams({ code });
      if (onlyActive) params.set('only_active', 'true');
      if (zoneFilter.trim()) params.set('zone', zoneFilter.trim());
      const r = await api.get(`/sous-traitants/tarifs/compare?${params}`);
      setComparison(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la comparaison');
    } finally { setLoadingComparison(false); }
  };

  useEffect(() => {
    if (selectedCode) loadComparison(selectedCode);
    // eslint-disable-next-line
  }, [onlyActive, zoneFilter]);

  const currentTrade = useMemo(
    () => catalog.find((t) => t.trade === selectedTrade),
    [catalog, selectedTrade]
  );
  const filteredPrestations = useMemo(() => {
    if (!currentTrade) return [];
    const q = prestationSearch.trim().toLowerCase();
    if (!q) return currentTrade.prestations;
    return currentTrade.prestations.filter((p) => p.label.toLowerCase().includes(q));
  }, [currentTrade, prestationSearch]);

  if (loading) return (
    <div className="min-h-[400px] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
    </div>
  );

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6" data-testid="tarifs-comparateur-page">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/sous-traitants" className="text-slate-500 hover:text-slate-800">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-blue-600" />
            Comparateur de tarifs
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Comparez les prix de plusieurs artisans pour une même prestation avant d'attribuer un chantier.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        {/* Colonne gauche : sélection */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 space-y-4">
          <div>
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Corps de métier</div>
            <div className="space-y-1 max-h-[280px] overflow-y-auto pr-1">
              {catalog.map((t) => {
                const withData = t.prestations.filter((p) => p.st_count > 0).length;
                return (
                  <button
                    key={t.trade}
                    type="button"
                    onClick={() => { setSelectedTrade(t.trade); setSelectedCode(null); setComparison(null); setPrestationSearch(''); }}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between gap-2 ${selectedTrade === t.trade ? 'bg-blue-50 text-blue-900 font-semibold' : 'text-slate-700 hover:bg-slate-50'}`}
                    data-testid={`compare-trade-${t.trade}`}
                  >
                    <span className="truncate">{t.trade}</span>
                    {withData > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-800 font-semibold flex-shrink-0">
                        {withData}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {currentTrade && (
            <div className="border-t border-slate-100 pt-4">
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Prestation</div>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={prestationSearch}
                  onChange={(e) => setPrestationSearch(e.target.value)}
                  placeholder="Rechercher…"
                  className="pl-9 h-9 text-sm"
                  data-testid="compare-search"
                />
              </div>
              <div className="space-y-1 max-h-[340px] overflow-y-auto pr-1">
                {filteredPrestations.map((p) => (
                  <button
                    key={p.code}
                    type="button"
                    onClick={() => loadComparison(p.code)}
                    disabled={p.st_count === 0}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between gap-2 ${
                      selectedCode === p.code
                        ? 'bg-blue-50 text-blue-900 font-semibold'
                        : p.st_count === 0
                          ? 'text-slate-400 cursor-not-allowed'
                          : 'text-slate-700 hover:bg-slate-50'
                    }`}
                    data-testid={`compare-prestation-${p.code}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{p.label}</div>
                      <div className="text-[10px] text-slate-400">{p.unite}</div>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0 ${p.st_count > 0 ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-400'}`}>
                      {p.st_count}
                    </span>
                  </button>
                ))}
                {filteredPrestations.length === 0 && (
                  <div className="text-xs text-slate-400 text-center py-4">Aucune prestation trouvée</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Colonne droite : résultats */}
        <div className="space-y-4">
          {/* Filtres */}
          {comparison && (
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 flex flex-wrap items-center gap-3" data-testid="compare-filters">
              <Filter className="w-4 h-4 text-slate-500" />
              <label className="text-sm text-slate-700 flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={onlyActive}
                  onChange={(e) => setOnlyActive(e.target.checked)}
                  data-testid="compare-only-active"
                />
                Uniquement les artisans actifs
              </label>
              <div className="relative flex-1 min-w-[180px]">
                <MapPin className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={zoneFilter}
                  onChange={(e) => setZoneFilter(e.target.value)}
                  placeholder="Filtrer par zone (ex : Paris)"
                  className="pl-8 h-9 text-sm"
                  data-testid="compare-zone-filter"
                />
              </div>
            </div>
          )}

          {loadingComparison ? (
            <div className="bg-white rounded-lg border border-slate-200 p-12 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : !comparison ? (
            <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
              <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">
                {selectedTrade
                  ? 'Sélectionnez une prestation pour lancer la comparaison'
                  : 'Sélectionnez un corps de métier puis une prestation'}
              </p>
            </div>
          ) : (
            <>
              {/* Header prestation + stats */}
              <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-lg border border-blue-100 p-4">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{comparison.meta?.trade}</div>
                    <h2 className="text-lg font-bold text-slate-900 mt-0.5">{comparison.meta?.label || comparison.code}</h2>
                    <div className="text-sm text-slate-600 mt-1">Unité : <b>{comparison.meta?.unite}</b></div>
                  </div>
                  {comparison.stats && (
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <StatBox label="Min" value={comparison.stats.min} icon={TrendingDown} accent="green" />
                      <StatBox label="Médiane" value={comparison.stats.median} />
                      <StatBox label="Moyenne" value={comparison.stats.avg} />
                      <StatBox label="Max" value={comparison.stats.max} icon={TrendingUp} accent="red" />
                    </div>
                  )}
                </div>
              </div>

              {/* Résultats */}
              {comparison.results.length === 0 ? (
                <div className="bg-white rounded-lg border border-slate-200 p-12 text-center text-sm text-slate-500">
                  Aucun artisan ne propose cette prestation avec les filtres actuels.
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid="compare-results">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">Artisan</th>
                        <th className="px-3 py-2 text-right font-semibold">Prix</th>
                        <th className="px-3 py-2 text-left font-semibold hidden md:table-cell">Zone</th>
                        <th className="px-3 py-2 text-right font-semibold hidden md:table-cell">Journalier</th>
                        <th className="px-3 py-2 text-right font-semibold hidden lg:table-cell">Horaire</th>
                        <th className="px-3 py-2 text-left font-semibold hidden lg:table-cell">MAJ</th>
                        <th className="px-3 py-2 text-right font-semibold w-24" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {comparison.results.map((r, i) => {
                        const isCheapest = i === 0 && comparison.results.length > 1;
                        return (
                          <tr key={r.sous_traitant_id} className={isCheapest ? 'bg-green-50' : ''} data-testid={`compare-row-${r.sous_traitant_id}`}>
                            <td className="px-3 py-3">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
                                    {r.nom}
                                    {isCheapest && (
                                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-600 text-white font-semibold">MOINS CHER</span>
                                    )}
                                  </div>
                                  <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                                    <span className={`px-1.5 py-0.5 rounded-full font-semibold ${STATUT_BADGE[r.statut_recrutement] || STATUT_BADGE.actif}`}>
                                      {STATUT_LABEL[r.statut_recrutement] || 'Actif'}
                                    </span>
                                    {r.note_qualite != null && (
                                      <span className="flex items-center gap-0.5 text-amber-600">
                                        <Star className="w-3 h-3 fill-current" />{r.note_qualite}
                                      </span>
                                    )}
                                    <span className="truncate">{r.specialite}</span>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-3 text-right whitespace-nowrap">
                              <div className={`text-lg font-bold tabular-nums ${isCheapest ? 'text-green-700' : 'text-slate-900'}`}>
                                {fmt(r.prestation.montant)}
                              </div>
                              <div className="text-[11px] text-slate-500">{r.prestation.unite}</div>
                            </td>
                            <td className="px-3 py-3 text-slate-600 hidden md:table-cell">
                              {r.zone ? (
                                <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" />{r.zone}</span>
                              ) : '—'}
                            </td>
                            <td className="px-3 py-3 text-right text-slate-600 tabular-nums hidden md:table-cell">
                              {r.tarif_journalier ? `${fmt(r.tarif_journalier)} €/j` : '—'}
                            </td>
                            <td className="px-3 py-3 text-right text-slate-600 tabular-nums hidden lg:table-cell">
                              {r.tarif_horaire ? `${fmt(r.tarif_horaire)} €/h` : '—'}
                            </td>
                            <td className="px-3 py-3 text-xs text-slate-500 hidden lg:table-cell">
                              {fmtDate(r.tarifs_date_maj) || '—'}
                            </td>
                            <td className="px-3 py-3 text-right">
                              <Link to={`/sous-traitants/${r.sous_traitant_id}`}>
                                <Button variant="outline" size="sm">
                                  <ExternalLink className="w-3 h-3 mr-1" /> Fiche
                                </Button>
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, icon: Icon, accent }) {
  const color = accent === 'green' ? 'text-green-700' : accent === 'red' ? 'text-red-700' : 'text-slate-800';
  return (
    <div className="bg-white rounded-md border border-slate-200 px-2 py-1.5 min-w-[70px]">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center justify-center gap-1">
        {Icon && <Icon className={`w-3 h-3 ${color}`} />}
        {label}
      </div>
      <div className={`text-base font-bold tabular-nums ${color}`}>{fmt(value)}</div>
    </div>
  );
}
